import { useAppStore } from "../../store/useAppStore";

/**
 * A deliberately tiny read-only mirror of the MyRise (tenant-facing)
 * consequence of DM actions here — NOT a MyRise implementation. Demonstrates
 * that Situation actions in FieldFlow change shared operational truth that
 * the customer app would also read. See FF01_WORKFLOW_COVERAGE.md "H. MyRise
 * support handoff" and "J. Move-in readiness".
 */
export function MyRisePreview({ unitId }: { unitId: string }) {
  const unit = useAppStore((s) => s.units.find((u) => u.id === unitId));
  const supportRequest = useAppStore((s) => [...s.supportRequests].reverse().find((r) => r.unitId === unitId));
  const moveInPlan = useAppStore((s) => s.moveInPlans.find((p) => p.unitId === unitId));
  if (!unit) return null;

  const readinessPercent = moveInPlan ? Math.round((moveInPlan.items.filter((i) => i.done).length / moveInPlan.items.length) * 100) : null;

  return (
    <section className="rounded-md border border-dashed border-brand-slate/40 bg-brand-ice/60 p-3.5" aria-label="MyRise tenant-side preview">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-brand-slate">MyRise · tenant view preview</p>
      <div className="mt-2 flex flex-col gap-2">
        {supportRequest && (
          <div className="flex items-center justify-between rounded-sm bg-white px-3 py-2 text-[13px]">
            <span className="text-foreground">"{supportRequest.subject}"</span>
            <span className="font-mono text-[10.5px] uppercase text-brand-blue">{supportRequest.status}</span>
          </div>
        )}
        {moveInPlan && readinessPercent !== null && (
          <div className="rounded-sm bg-white px-3 py-2">
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-foreground">Move-in readiness</span>
              <span className="font-mono text-[11px] text-brand-blue">{readinessPercent}%</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-brand-soft-slate">
              <div className="h-1.5 rounded-full bg-brand-blue transition-all" style={{ width: `${readinessPercent}%` }} />
            </div>
            {readinessPercent === 100 && (
              <p className="mt-1.5 font-body-highlight text-[12px] font-black text-success">Your space is ready — welcome home.</p>
            )}
          </div>
        )}
        {!supportRequest && !moveInPlan && <p className="text-[12.5px] text-brand-slate">No tenant-visible state for this unit yet.</p>}
      </div>
    </section>
  );
}
